/* Public FAQ — fed by the single source backend/data/faq/faq.json (src/content/faqSource.js). */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Header } from '@/components/Header';
import { SEO, breadcrumbJsonLd } from '@/components/SEO';
import { Footer } from '@/components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  LINK_PATHS, SURFACES, categories as getCategories, entriesFor, lastVerified, normalizeLang, searchEntries,
} from '@/content/faqSource';

const AnswerLinks = ({ links, t }) => {
  if (!links?.length) return null;
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1" data-testid="faq-links">
      {links.map((l) => (
        <li key={`${l.type}-${l.id}`}>
          <Link to={LINK_PATHS[l.type][l.id]} className="text-sm font-medium text-accent underline underline-offset-2 hover:text-accent-hover"
            data-testid={`faq-link-${l.type}-${l.id}`}>
            {t(`faq.links.${l.type}.${l.id}`)}
          </Link>
        </li>
      ))}
    </ul>
  );
};

const FaqList = ({ entries, open, setOpen, t, showCategory, catTitles }) => (
  <Accordion type="multiple" value={open} onValueChange={setOpen} className="w-full">
    {entries.map((e) => (
      <AccordionItem key={e.id} value={e.id} id={`faq-${e.id}`} className="scroll-mt-24" data-testid={`faq-item-${e.id}`}>
        <AccordionTrigger className="text-left text-base font-medium text-primary hover:no-underline">
          <span>
            {showCategory && <span className="block text-xs font-normal text-ink-muted mb-0.5">{catTitles[e.category]}</span>}
            {e.q}
          </span>
        </AccordionTrigger>
        <AccordionContent className="text-slate-600 leading-relaxed text-sm sm:text-base">
          <p>{e.a}</p>
          <AnswerLinks links={e.links} t={t} />
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export default function FAQPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = normalizeLang(i18n.resolvedLanguage || i18n.language);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState([]);
  const searchRef = useRef(null);

  const cats = useMemo(() => getCategories(lang), [lang]);
  const catTitles = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c.title])), [cats]);
  const all = useMemo(() => entriesFor(SURFACES.faq, lang), [lang]);
  const results = useMemo(() => (query.trim() ? searchEntries(all, query) : null), [all, query]);
  const verified = lastVerified();

  // Deep link: /faq#faq-<id> opens that question and scrolls to it.
  useEffect(() => {
    const id = (location.hash || '').replace(/^#faq-/, '');
    if (id && all.some((e) => e.id === id)) {
      setOpen((cur) => (cur.includes(id) ? cur : [...cur, id]));
      setTimeout(() => document.getElementById(`faq-${id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }), 60);
    }
  }, [location.hash, all]);

  const jumpTo = (catId) => document.getElementById(`faq-cat-${catId}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const jsonLd = [
    breadcrumbJsonLd([{ name: t('seo.breadcrumb.home'), path: '/' }, { name: t('header.faq'), path: '/faq' }]),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: all.map((e) => ({ '@type': 'Question', name: e.q, acceptedAnswer: { '@type': 'Answer', text: e.a } })),
    },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO title={t('seo.faq.title')} description={t('seo.faq.description')} jsonLd={jsonLd} />
      <Header />
      <main className="flex-1 py-12 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="faq-page">
          <h1 className="text-4xl sm:text-5xl font-bold text-primary tracking-tight">{t('faq.pageTitle')}</h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{t('faq.pageIntro')}</p>

          <div className="mt-8 relative" role="search">
            <label htmlFor="faq-search" className="sr-only">{t('faq.searchLabel')}</label>
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
            <input
              id="faq-search" ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t('faq.searchPlaceholder')} maxLength={120} autoComplete="off" enterKeyHint="search"
              className="w-full h-12 rounded-xl border border-slate-200 pl-10 pr-10 text-base focus:outline-none focus:ring-2 focus:ring-accent"
              data-testid="faq-search"
            />
            {query && (
              <button type="button" onClick={() => { setQuery(''); searchRef.current?.focus(); }} aria-label={t('faq.searchClear')}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-slate-500" data-testid="faq-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {!results && (
            <nav aria-label={t('faq.categoriesLabel')} className="mt-5 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="faq-categories">
              <ul className="flex gap-2 overflow-x-auto pb-2 snap-x [scrollbar-width:thin]">
                {cats.map((c) => (
                  <li key={c.id} className="snap-start shrink-0">
                    <button type="button" onClick={() => jumpTo(c.id)}
                      className="min-h-11 px-4 rounded-full border border-slate-200 bg-white text-sm font-medium text-primary hover:border-accent whitespace-nowrap"
                      data-testid={`faq-category-${c.id}`}>
                      {c.title}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {results ? (
            <section className="mt-8" data-testid="faq-results" aria-live="polite">
              <p className="text-sm text-ink-muted mb-2" data-testid="faq-results-count">{t('faq.resultsCount', { count: results.length })}</p>
              {results.length > 0 ? (
                <FaqList entries={results} open={open} setOpen={setOpen} t={t} showCategory catTitles={catTitles} />
              ) : (
                <div className="rounded-xl bg-slate-50 p-5" data-testid="faq-no-results">
                  <p className="font-medium text-primary">{t('faq.noResultsTitle')}</p>
                  <p className="mt-1 text-sm text-slate-600">{t('faq.noResultsBody')}</p>
                  <Link to="/contact" className="mt-3 inline-block text-sm font-medium text-accent underline">{t('faq.contactCta')}</Link>
                </div>
              )}
            </section>
          ) : (
            cats.map((c) => {
              const items = all.filter((e) => e.category === c.id);
              if (!items.length) return null;
              return (
                <section key={c.id} id={`faq-cat-${c.id}`} className="mt-10 scroll-mt-24" data-testid={`faq-section-${c.id}`}>
                  <h2 className="text-2xl sm:text-3xl font-semibold text-primary mb-2">{c.title}</h2>
                  <FaqList entries={items} open={open} setOpen={setOpen} t={t} catTitles={catTitles} />
                </section>
              );
            })
          )}

          <aside className="mt-14 rounded-2xl bg-slate-50 p-6" data-testid="faq-help">
            <h2 className="text-xl font-semibold text-primary">{t('faq.helpTitle')}</h2>
            <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">{t('faq.helpBody')}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/contact" className="min-h-11 inline-flex items-center px-5 rounded-xl bg-accent text-white font-medium hover:bg-accent-hover" data-testid="faq-contact-cta">
                {t('faq.contactCta')}
              </Link>
              <Link to="/conditions-generales" className="min-h-11 inline-flex items-center px-5 rounded-xl border border-slate-300 text-primary font-medium hover:border-accent" data-testid="faq-terms-cta">
                {t('faq.termsCta')}
              </Link>
            </div>
            <p className="mt-4 text-xs text-ink-muted" data-testid="faq-verified">{t('faq.updated', { date: verified })}</p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}
